import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import usePersistedState from './usePersistedState';
import config from 'config';
import TagManager from 'react-gtm-module';
import { registrationActions } from 'careand-redux/slices/registration';
import { useDispatch } from 'react-redux';
import { api, updateTrackingHeaders } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import { getActivePath } from 'careand-redux/slices/ui/navigation';
import ReactPixel from 'react-facebook-pixel';
import { getAnalyticsConfig } from 'services/portalData';

/** Tracking is on only when this tenant has a configured Matomo instance. */
const matomoEnabled = () => {
  const analytics = getAnalyticsConfig();
  return !!analytics?.url && !!analytics.site_id;
};

function extractClickId(cookieString, platform) {
  switch (platform.toLowerCase()) {
    case 'google': {
      // the click id is alphanumeric (`_gcl_aw=GCL.<timestamp>.<gclid>`)
      const gclMatch = cookieString.match(/_gcl_aw=GCL\.\d+\.([^;]+)/);
      return gclMatch ? gclMatch[1] : null;
    }
    case 'fbc': {
      const fbcMatch = cookieString.match(/_fbc=([^;]+)/);
      return fbcMatch ? fbcMatch[1] : null;
    }
    case 'fbp': {
      const fbpMatch = cookieString.match(/_fbp=([^;]+)/);
      return fbpMatch ? fbpMatch[1] : null;
    }
    default: {
      return null;
    }
  }
}

const DEFAULT_OBJECT = {};

const useAnalytics = () => {
  const dispatch = useDispatch();
  const activePath = useAppSelector((state) => getActivePath(state.navigation));
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);
  const patient = useAppSelector((state) => state.patients.byId[activeUserId]);
  const hasAdTrackingPermission = useAppSelector((state) => {
    const role = state.authentication.role;
    // a read-only viewAs session is staff previewing the patient portal: it must
    // neither identify the patient to ad networks nor write to their record
    return (!role || role === 'patient') && !state.authentication.readOnly;
  });
  const [trackingIds, setTrackingIds] = usePersistedState('trackingIds', DEFAULT_OBJECT);

  const [matomoTrackId, setMatomoTrackId] = useState();
  const googleClickId = extractClickId(document.cookie, 'google');
  const metaClickId = extractClickId(document.cookie, 'fbc');
  const metaPixelId = extractClickId(document.cookie, 'fbp');

  useEffect(() => {
    updateTrackingHeaders(trackingIds);
  }, [trackingIds]);

  useEffect(() => {
    if (!hasAdTrackingPermission || !config.FACEBOOK_PIXEL) {
      return;
    }
    // init before the first pageView, even signed out: a pageView against an
    // uninitialized pixel is dropped, and those are the landing/registration hits
    ReactPixel.init(
      config.FACEBOOK_PIXEL,
      patient
        ? {
            em: patient.email,
            external_id: patient.id,
          }
        : {},
      {
        autoConfig: true,
        debug: config.ENVIRONMENT !== 'production',
      },
    );
  }, [patient?.id, patient?.email, hasAdTrackingPermission]);

  // updatePatient is a full-record PUT, so every tracking id the hook stamps has
  // to go out in ONE write, and only when it actually changes something.
  const stampedRef = useRef(null);

  useEffect(() => {
    if (!patient || !hasAdTrackingPermission) {
      return;
    }
    let cancelled = false;

    const stampTrackingIds = async () => {
      const updates = {};

      // absent _fbc/_fbp cookies (expired, second device, Safari ITP) must not
      // erase attribution already stored on the record
      const storedMeta = patient.meta_tracking;
      const pixelId = metaPixelId ?? storedMeta?.pixelId ?? null;
      const clickId = metaClickId ?? storedMeta?.clickId ?? null;
      if (pixelId !== (storedMeta?.pixelId ?? null) || clickId !== (storedMeta?.clickId ?? null)) {
        updates.meta_tracking = { pixelId, clickId };
      }

      if (matomoEnabled() && !patient.matomo_id) {
        const matomo_id = await getMatomoVisitorId();
        if (matomo_id) {
          updates.matomo_id = matomo_id;
        }
      }

      if (cancelled || !Object.keys(updates).length) {
        return;
      }

      // the patients slice hands back a fresh object on every response, so this
      // effect re-runs constantly; only re-issue the PUT if the payload changed,
      // otherwise a backend that does not echo the ids back loops forever
      const signature = JSON.stringify([patient.id, updates]);
      if (stampedRef.current === signature) {
        return;
      }
      stampedRef.current = signature;

      dispatch(
        api.Patients.updatePatient({
          patient: { ...patient, ...updates },
          id: patient.id,
        }),
      );
    };

    stampTrackingIds();

    return () => {
      cancelled = true;
    };
  }, [metaClickId, metaPixelId, patient, hasAdTrackingPermission]);

  useEffect(() => {
    // Keep-latest merge, re-run after the persisted load replaces state
    // (trackingIds dep): a fresh cookie id wins, an absent one keeps the stored
    // id — neither side of the boot race may erase the other. The bail-out on
    // no-change is what stops the trackingIds dep from looping.
    setTrackingIds((prevState) => {
      const next = {
        ...prevState,
        gclid: googleClickId ?? prevState.gclid ?? null,
        matomo_id: matomoTrackId ?? prevState.matomo_id,
        fbc: metaClickId ?? prevState.fbc ?? null,
        fbp: metaPixelId ?? prevState.fbp ?? null,
      };
      const changed = Object.keys(next).some((key) => next[key] !== prevState[key]);
      return changed ? next : prevState;
    });
  }, [googleClickId, matomoTrackId, metaClickId, metaPixelId, trackingIds, setTrackingIds]);

  useEffect(() => {
    initializeGoogleTagManager();
    initializeMatomo();
  }, [activeUserId]);

  const gtmInitializedRef = useRef(false);
  const matomoInitializedRef = useRef(false);

  const initializeGoogleTagManager = () => {
    if (config.GOOGLE_TAG_ID && hasAdTrackingPermission && !gtmInitializedRef.current) {
      gtmInitializedRef.current = true;
      TagManager.initialize({ gtmId: config.GOOGLE_TAG_ID });
    }
  };

  useEffect(() => {
    let cancelled = false;
    getMatomoVisitorId().then((track_id) => {
      if (cancelled || !track_id) {
        return;
      }
      setMatomoTrackId(track_id);
      dispatch(registrationActions.setTrackId(track_id));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const initializeMatomo = () => {
    // The tenant's own Matomo instance, from portal-data (available pre-auth).
    // No analytics module configured => no tracking at all.
    const analytics = getAnalyticsConfig();
    if (analytics?.url && analytics.site_id && !matomoInitializedRef.current) {
      matomoInitializedRef.current = true;
      const url = analytics.url;
      window._paq = window._paq || [];

      // the tracker drains _paq in order, so it has to know where to send hits
      // before anything is tracked
      matomoPushInstruction('setTrackerUrl', url + (analytics.tracker_path || 'matomo.php'));
      matomoPushInstruction('setSiteId', analytics.site_id);
      if (analytics.domains?.length) {
        matomoPushInstruction('setDomains', analytics.domains);
        matomoPushInstruction('enableCrossDomainLinking');
      }
      matomoPushInstruction('trackPageView');
      matomoPushInstruction('enableLinkTracking');

      const d = document;
      const g = d.createElement('script');
      const s = d.getElementsByTagName('script')[0];
      g.async = true;
      g.src = url + (analytics.script_path || 'matomo.js');
      if (s?.parentNode) {
        s.parentNode.insertBefore(g, s);
      } else {
        (d.head || d.documentElement).appendChild(g);
      }
    }
  };

  const matomoPushInstruction = (eventName, ...vals) => {
    try {
      if (window._paq) {
        window._paq.push([eventName, ...vals]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getMatomoVisitorId = async () => {
    if (matomoEnabled()) {
      if (matomoTrackId) return matomoTrackId;
      if (window._paq) {
        return new Promise((resolve) => {
          window._paq.push([
            function () {
              resolve(this.getVisitorId());
            },
          ]);
        });
      }
    }
  };

  useEffect(() => {
    trackPageView(activePath, patient);
    // keyed on the fields the page view actually uses: the patient object gets a
    // fresh identity on every patient-touching response, which would double-count
  }, [activePath, patient?.id, patient?.matomo_id]);

  const trackPageView = (pathname, patient) => {
    if (hasAdTrackingPermission) {
      ReactPixel.pageView();
      trackTagManager('pageview', { pagePath: pathname });
    }
    trackMatomo(pathname, patient);
  };

  const trackMatomo = (pathname, patient) => {
    if (matomoEnabled()) {
      if (patient?.matomo_id) {
        matomoPushInstruction('setVisitorId', patient.matomo_id);
      }
      matomoPushInstruction('setCustomUrl', pathname);
      matomoPushInstruction('trackPageView');
    }
  };

  const trackTagManager = useCallback(
    (event, data = {}) => {
      if (!!config.GOOGLE_TAG_ID) {
        // Base data layer object
        const dataLayerObject = {
          event,
          user_data: patient
            ? {
                email_address: patient.email,
              }
            : undefined,
          ...data,
        };

        if (dataLayerObject.value) {
          dataLayerObject.currency = 'CAD';
        }

        TagManager.dataLayer({
          dataLayer: dataLayerObject,
        });
      }
    },
    [patient],
  );

  // fed straight into AnalyticsContext.Provider: a new identity per render would
  // re-render every consumer
  return useMemo(
    () => ({
      trackingIds,
      trackTagManager,
    }),
    [trackingIds, trackTagManager],
  );
};

export default useAnalytics;
